import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  FileText,
  X,
  CheckCircle,
  Scissors,
  Combine,
  Split,
  Trash2,
  Move,
  Scan,
  Download,
  ArrowRight,
  Shield,
  Zap,
  Plus,
  Eye,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  Maximize,
  Minimize,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Loader,
  GripVertical,
  ArrowUp,
  ArrowDown,
  Square,
  CheckSquare,
  Shuffle
} from 'lucide-react';

// Componente de visor de PDF con renderizado real
const SimplePdfViewer = ({ file, currentPage, onPageChange, zoom }) => {
  const canvasRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actualPages, setActualPages] = useState(0);

  useEffect(() => {
    const loadPdf = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!window.pdfjsLib) {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
          script.async = true;

          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });

          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        }

        const loadingTask = window.pdfjsLib.getDocument(file.url);
        const pdf = await loadingTask.promise;
        setPdfDoc(pdf);
        setActualPages(pdf.numPages);

        if (file.pages !== pdf.numPages) {
          file.pages = pdf.numPages;
        }

        setIsLoading(false);
      } catch (err) {
        console.error('Error al cargar PDF:', err);
        setError('No se pudo cargar el PDF');
        setIsLoading(false);
      }
    };

    loadPdf();
  }, [file.url]);

  useEffect(() => {
    const renderPage = async () => {
      if (!pdfDoc || !canvasRef.current) return;

      try {
        const page = await pdfDoc.getPage(currentPage);
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        const containerWidth = canvas.parentElement?.clientWidth || 800;
        const defaultViewport = page.getViewport({ scale: 1 });
        const scale = Math.min((containerWidth - 32) / defaultViewport.width, 2) * (zoom / 100);
        const viewport = page.getViewport({ scale });

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        context.clearRect(0, 0, canvas.width, canvas.height);
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
          intent: 'display'
        };

        if (canvas.renderTask) {
          canvas.renderTask.cancel();
        }

        const renderTask = page.render(renderContext);
        canvas.renderTask = renderTask;

        await renderTask.promise;
        canvas.renderTask = null;
      } catch (err) {
        if (err.name !== 'RenderingCancelledException') {
          console.error('Error al renderizar página:', err);
        }
      }
    };

    renderPage();

    return () => {
      if (canvasRef.current?.renderTask) {
        canvasRef.current.renderTask.cancel();
      }
    };
  }, [pdfDoc, currentPage, zoom]);

  if (isLoading) {
    return (
      <div className="w-full h-96 bg-white shadow-2xl rounded-xl overflow-hidden flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-red-500 mx-auto mb-4 animate-spin" />
          <p className="text-slate-600 font-semibold">Cargando PDF...</p>
          <p className="text-slate-500 text-sm mt-2">Por favor espera un momento</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-96 bg-white shadow-2xl rounded-xl overflow-hidden flex items-center justify-center">
        <div className="text-center p-8">
          <FileText className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <p className="text-red-600 font-semibold mb-2">{error}</p>
          <p className="text-slate-500 text-sm">
            Intenta con otro archivo o descárgalo para verlo
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-2xl rounded-xl overflow-hidden w-full h-full flex flex-col max-w-full">
      <div className="h-12 bg-slate-800 text-white flex items-center justify-center font-mono text-xs sm:text-sm flex-shrink-0 px-2">
        <span className="truncate">{file.name}</span>
        <span className="mx-2 hidden sm:inline">-</span>
        <span className="ml-2 sm:ml-0">Pág. {currentPage}/{actualPages}</span>
      </div>
      <div className="flex-1 bg-slate-100 flex items-start justify-center p-2 sm:p-4 overflow-auto">
        <canvas
          ref={canvasRef}
          className="shadow-lg max-w-full h-auto"
        />
      </div>
    </div>
  );
};

// Componente para reordenar PDFs
const PdfReorderList = ({ files, onReorder, onRemove }) => {
  const [draggedIndex, setDraggedIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) return;

    const newFiles = [...files];
    const [movedFile] = newFiles.splice(draggedIndex, 1);
    newFiles.splice(targetIndex, 0, movedFile);

    onReorder(newFiles);
    setDraggedIndex(null);
  };

  const moveUp = (index) => {
    if (index === 0) return;
    const newFiles = [...files];
    [newFiles[index], newFiles[index - 1]] = [newFiles[index - 1], newFiles[index]];
    onReorder(newFiles);
  };

  const moveDown = (index) => {
    if (index === files.length - 1) return;
    const newFiles = [...files];
    [newFiles[index], newFiles[index + 1]] = [newFiles[index + 1], newFiles[index]];
    onReorder(newFiles);
  };

  return (
    <div className="space-y-2">
      {files.map((file, index) => (
        <motion.div
          key={file.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className={`flex items-center gap-3 p-3 bg-white rounded-xl border-2 transition-all ${draggedIndex === index
              ? 'border-blue-500 bg-blue-50 shadow-lg'
              : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
            }`}
          draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDrop={(e) => handleDrop(e, index)}
        >
          {/* Handle de arrastre */}
          <div className="flex flex-col items-center gap-1">
            <GripVertical className="w-4 h-4 text-slate-400 cursor-grab" />
            <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-2 py-1 rounded-full">
              {index + 1}
            </span>
          </div>

          {/* Icono del PDF */}
          <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-rose-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-white" />
          </div>

          {/* Información del archivo */}
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-slate-800 truncate text-sm">
              {file.name}
            </h4>
            <div className="flex items-center gap-2 text-xs text-slate-600 mt-1">
              <span>{file.size}</span>
              <span>•</span>
              <span>{file.pages} páginas</span>
            </div>
          </div>

          {/* Controles de orden */}
          <div className="flex items-center gap-1">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => moveUp(index)}
              disabled={index === 0}
              className={`p-1 rounded-lg transition-colors ${index === 0
                  ? 'text-slate-300 cursor-not-allowed'
                  : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              title="Mover arriba"
            >
              <ArrowUp className="w-4 h-4" />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => moveDown(index)}
              disabled={index === files.length - 1}
              className={`p-1 rounded-lg transition-colors ${index === files.length - 1
                  ? 'text-slate-300 cursor-not-allowed'
                  : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              title="Mover abajo"
            >
              <ArrowDown className="w-4 h-4" />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => onRemove(file.id)}
              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Eliminar archivo"
            >
              <X className="w-4 h-4" />
            </motion.button>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// Componente para dividir PDF con miniaturas
const PdfSplitInterface = ({ file, onSplit, onCancel }) => {
  const [ranges, setRanges] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [pageThumbnails, setPageThumbnails] = useState([]);
  const [isLoadingThumbnails, setIsLoadingThumbnails] = useState(true);
  const [currentRange, setCurrentRange] = useState({ start: 1, end: 1 });

  // Cargar miniaturas del PDF
  useEffect(() => {
    const loadThumbnails = async () => {
      if (!file || !file.url) return;

      try {
        setIsLoadingThumbnails(true);

        if (!window.pdfjsLib) {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
          script.async = true;
          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        }

        const loadingTask = window.pdfjsLib.getDocument(file.url);
        const pdf = await loadingTask.promise;
        const totalPages = pdf.numPages;

        // Crear miniaturas para cada página
        const thumbnails = [];
        for (let i = 1; i <= Math.min(totalPages, 50); i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 0.2 });

          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({
            canvasContext: context,
            viewport: viewport
          }).promise;

          thumbnails.push({
            pageNumber: i,
            dataUrl: canvas.toDataURL(),
            width: viewport.width,
            height: viewport.height
          });
        }

        setPageThumbnails(thumbnails);
        setCurrentRange({ start: 1, end: totalPages });

      } catch (error) {
        console.error('Error cargando miniaturas:', error);
      } finally {
        setIsLoadingThumbnails(false);
      }
    };

    loadThumbnails();
  }, [file]);

  const addRange = () => {
    if (currentRange.start > 0 && currentRange.end <= file.pages && currentRange.start <= currentRange.end) {
      const newRange = `${currentRange.start}-${currentRange.end}`;
      if (!ranges.includes(newRange)) {
        setRanges([...ranges, newRange]);
      }
      setCurrentRange({ start: currentRange.end + 1, end: file.pages });
    }
  };

  const removeRange = (index) => {
    const newRanges = ranges.filter((_, i) => i !== index);
    setRanges(newRanges);
  };

  const handleSplit = () => {
    if (ranges.length === 0) {
      alert('Por favor, agrega al menos un rango de páginas');
      return;
    }
    onSplit(ranges.join(','));
  };

  const selectPage = (pageNumber) => {
    setSelectedPages(prev => {
      const isSelected = prev.includes(pageNumber);
      if (isSelected) {
        return prev.filter(p => p !== pageNumber);
      } else {
        return [...prev, pageNumber].sort((a, b) => a - b);
      }
    });
  };

  const addSelectedPagesAsRange = () => {
    if (selectedPages.length === 0) return;

    // Convertir páginas seleccionadas en rangos consecutivos
    const newRanges = [];
    let start = selectedPages[0];

    for (let i = 1; i <= selectedPages.length; i++) {
      if (i === selectedPages.length || selectedPages[i] !== selectedPages[i - 1] + 1) {
        const end = selectedPages[i - 1];
        if (start === end) {
          newRanges.push(`${start}`);
        } else {
          newRanges.push(`${start}-${end}`);
        }
        start = selectedPages[i];
      }
    }

    setRanges([...ranges, ...newRanges]);
    setSelectedPages([]);
  };

  const selectAllPages = () => {
    const allPages = Array.from({ length: file.pages }, (_, i) => i + 1);
    setSelectedPages(allPages);
  };

  const clearSelection = () => {
    setSelectedPages([]);
  };

  const createRangeFromSelection = () => {
    if (selectedPages.length === 0) {
      setCurrentRange({ start: 1, end: file.pages });
    } else {
      const minPage = Math.min(...selectedPages);
      const maxPage = Math.max(...selectedPages);
      setCurrentRange({ start: minPage, end: maxPage });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl p-6 shadow-2xl border border-slate-200"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-bold text-slate-800 text-xl mb-2">
            Dividir PDF: {file.name}
          </h3>
          <p className="text-slate-600">
            Selecciona los rangos de páginas que deseas extraer en PDFs separados
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onCancel}
            className="px-4 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-xl font-semibold transition-colors"
          >
            Cancelar
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleSplit}
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold transition-colors"
          >
            Dividir PDF
          </motion.button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Panel izquierdo - Selección de rangos */}
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-xl p-4">
            <h4 className="font-semibold text-slate-800 mb-3">Agregar Rango de Páginas</h4>
            <div className="flex items-center space-x-3 mb-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Desde página:
                </label>
                <input
                  type="number"
                  min="1"
                  max={file.pages}
                  value={currentRange.start}
                  onChange={(e) => setCurrentRange(prev => ({
                    ...prev,
                    start: Math.max(1, Math.min(file.pages, parseInt(e.target.value) || 1))
                  }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Hasta página:
                </label>
                <input
                  type="number"
                  min={currentRange.start}
                  max={file.pages}
                  value={currentRange.end}
                  onChange={(e) => setCurrentRange(prev => ({
                    ...prev,
                    end: Math.max(currentRange.start, Math.min(file.pages, parseInt(e.target.value) || currentRange.start))
                  }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={addRange}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-2 rounded-lg font-semibold transition-colors"
            >
              Agregar Rango ({currentRange.start}-{currentRange.end})
            </motion.button>
          </div>

          {/* Selección rápida */}
          <div className="bg-blue-50 rounded-xl p-4">
            <h4 className="font-semibold text-slate-800 mb-3">Selección Rápida</h4>
            <div className="flex flex-wrap gap-2 mb-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={selectAllPages}
                className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium"
              >
                Seleccionar Todas
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={clearSelection}
                className="px-3 py-1 bg-slate-500 hover:bg-slate-600 text-white rounded-lg text-sm font-medium"
              >
                Limpiar
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={createRangeFromSelection}
                className="px-3 py-1 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium"
              >
                Usar Selección
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={addSelectedPagesAsRange}
                className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium"
              >
                Agregar Selección
              </motion.button>
            </div>
            {selectedPages.length > 0 && (
              <p className="text-sm text-slate-600">
                {selectedPages.length} página(s) seleccionada(s): {selectedPages.join(', ')}
              </p>
            )}
          </div>

          {/* Rangos agregados */}
          <div className="bg-slate-50 rounded-xl p-4">
            <h4 className="font-semibold text-slate-800 mb-3">Rangos a Dividir</h4>
            {ranges.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">
                No hay rangos agregados. Agrega al menos un rango de páginas.
              </p>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {ranges.map((range, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200"
                  >
                    <span className="font-mono text-slate-700">
                      {range.replace('-', ' a ')}
                    </span>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => removeRange(index)}
                      className="p-1 text-red-400 hover:text-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </motion.button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Panel derecho - Miniaturas de páginas */}
        <div className="bg-slate-50 rounded-xl p-4">
          <h4 className="font-semibold text-slate-800 mb-3">
            Vista Previa ({file.pages} páginas)
          </h4>

          {isLoadingThumbnails ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-8 h-8 text-emerald-500 animate-spin mr-2" />
              <span className="text-slate-600">Cargando miniaturas...</span>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2 max-h-96 overflow-y-auto">
              {Array.from({ length: file.pages }, (_, i) => i + 1).map(pageNumber => (
                <motion.div
                  key={pageNumber}
                  whileHover={{ scale: 1.05 }}
                  className={`relative cursor-pointer rounded-lg border-2 transition-all ${selectedPages.includes(pageNumber)
                      ? 'border-emerald-500 bg-emerald-50 shadow-md'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  onClick={() => selectPage(pageNumber)}
                >
                  <div className="p-2">
                    <div className="bg-slate-100 rounded aspect-[3/4] flex items-center justify-center mb-1">
                      {pageThumbnails.find(t => t.pageNumber === pageNumber) ? (
                        <img
                          src={pageThumbnails.find(t => t.pageNumber === pageNumber).dataUrl}
                          alt={`Página ${pageNumber}`}
                          className="w-full h-full object-contain rounded"
                        />
                      ) : (
                        <FileText className="w-6 h-6 text-slate-400" />
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-700">
                        {pageNumber}
                      </span>
                      {selectedPages.includes(pageNumber) ? (
                        <CheckSquare className="w-3 h-3 text-emerald-500" />
                      ) : (
                        <Square className="w-3 h-3 text-slate-400" />
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
        <div className="flex items-center space-x-2 text-amber-800">
          <Zap className="w-4 h-4" />
          <span className="text-sm font-medium">
            Consejo: Puedes agregar múltiples rangos (ej: 1-3, 5-7, 10) para crear varios PDFs
          </span>
        </div>
      </div>
    </motion.div>
  );
};

// Componente para eliminar páginas de PDF
const PdfDeletePagesInterface = ({ file, onDeletePages, onCancel }) => {
  const [selectedPages, setSelectedPages] = useState([]);
  const [pageThumbnails, setPageThumbnails] = useState([]);
  const [isLoadingThumbnails, setIsLoadingThumbnails] = useState(true);

  // Cargar miniaturas del PDF
  useEffect(() => {
    const loadThumbnails = async () => {
      if (!file || !file.url) return;

      try {
        setIsLoadingThumbnails(true);

        if (!window.pdfjsLib) {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
          script.async = true;
          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        }

        const loadingTask = window.pdfjsLib.getDocument(file.url);
        const pdf = await loadingTask.promise;
        const totalPages = pdf.numPages;

        // Crear miniaturas para cada página
        const thumbnails = [];
        for (let i = 1; i <= Math.min(totalPages, 50); i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 0.2 });

          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({
            canvasContext: context,
            viewport: viewport
          }).promise;

          thumbnails.push({
            pageNumber: i,
            dataUrl: canvas.toDataURL(),
            width: viewport.width,
            height: viewport.height
          });
        }

        setPageThumbnails(thumbnails);

      } catch (error) {
        console.error('Error cargando miniaturas:', error);
      } finally {
        setIsLoadingThumbnails(false);
      }
    };

    loadThumbnails();
  }, [file]);

  const selectPage = (pageNumber) => {
    setSelectedPages(prev => {
      const isSelected = prev.includes(pageNumber);
      if (isSelected) {
        return prev.filter(p => p !== pageNumber);
      } else {
        return [...prev, pageNumber].sort((a, b) => a - b);
      }
    });
  };

  const selectAllPages = () => {
    const allPages = Array.from({ length: file.pages }, (_, i) => i + 1);
    setSelectedPages(allPages);
  };

  const clearSelection = () => {
    setSelectedPages([]);
  };

  const selectOddPages = () => {
    const oddPages = Array.from({ length: file.pages }, (_, i) => i + 1).filter(page => page % 2 === 1);
    setSelectedPages(oddPages);
  };

  const selectEvenPages = () => {
    const evenPages = Array.from({ length: file.pages }, (_, i) => i + 1).filter(page => page % 2 === 0);
    setSelectedPages(evenPages);
  };

  const handleDeletePages = () => {
    if (selectedPages.length === 0) {
      alert('Por favor, selecciona al menos una página para eliminar');
      return;
    }

    if (selectedPages.length === file.pages) {
      alert('No puedes eliminar todas las páginas del PDF');
      return;
    }

    onDeletePages(selectedPages);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl p-6 shadow-2xl border border-slate-200"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-bold text-slate-800 text-xl mb-2">
            Eliminar Páginas: {file.name}
          </h3>
          <p className="text-slate-600">
            Selecciona las páginas que deseas eliminar del PDF
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onCancel}
            className="px-4 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-xl font-semibold transition-colors"
          >
            Cancelar
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleDeletePages}
            disabled={selectedPages.length === 0}
            className={`px-4 py-2 rounded-xl font-semibold transition-colors ${selectedPages.length === 0
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-red-500 hover:bg-red-600 text-white'
              }`}
          >
            Eliminar ({selectedPages.length}) Páginas
          </motion.button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Panel izquierdo - Controles de selección */}
        <div className="space-y-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <h4 className="font-semibold text-red-800 mb-3">Selección Rápida</h4>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={selectAllPages}
                className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium"
              >
                Seleccionar Todas
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={clearSelection}
                className="px-3 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-lg text-sm font-medium"
              >
                Limpiar Selección
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={selectOddPages}
                className="px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium"
              >
                Páginas Impares
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={selectEvenPages}
                className="px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium"
              >
                Páginas Pares
              </motion.button>
            </div>
            {selectedPages.length > 0 && (
              <div className="mt-3">
                <p className="text-sm text-red-700 font-medium mb-2">
                  {selectedPages.length} página(s) seleccionada(s) para eliminar:
                </p>
                <div className="max-h-24 overflow-y-auto">
                  <div className="flex flex-wrap gap-1">
                    {selectedPages.map(page => (
                      <span
                        key={page}
                        className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-medium"
                      >
                        {page}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-slate-50 rounded-xl p-4">
            <h4 className="font-semibold text-slate-800 mb-3">Información</h4>
            <div className="space-y-2 text-sm text-slate-600">
              <p>• Total de páginas: <strong>{file.pages}</strong></p>
              <p>• Páginas a eliminar: <strong className="text-red-600">{selectedPages.length}</strong></p>
              <p>• Páginas restantes: <strong className="text-green-600">{file.pages - selectedPages.length}</strong></p>
            </div>
          </div>
        </div>

        {/* Panel derecho - Miniaturas de páginas */}
        <div className="bg-slate-50 rounded-xl p-4">
          <h4 className="font-semibold text-slate-800 mb-3">
            Selecciona Páginas a Eliminar ({file.pages} páginas)
          </h4>

          {isLoadingThumbnails ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-8 h-8 text-red-500 animate-spin mr-2" />
              <span className="text-slate-600">Cargando miniaturas...</span>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2 max-h-96 overflow-y-auto">
              {Array.from({ length: file.pages }, (_, i) => i + 1).map(pageNumber => (
                <motion.div
                  key={pageNumber}
                  whileHover={{ scale: 1.05 }}
                  className={`relative cursor-pointer rounded-lg border-2 transition-all ${selectedPages.includes(pageNumber)
                      ? 'border-red-500 bg-red-50 shadow-md'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  onClick={() => selectPage(pageNumber)}
                >
                  <div className="p-2">
                    <div className="bg-slate-100 rounded aspect-[3/4] flex items-center justify-center mb-1">
                      {pageThumbnails.find(t => t.pageNumber === pageNumber) ? (
                        <img
                          src={pageThumbnails.find(t => t.pageNumber === pageNumber).dataUrl}
                          alt={`Página ${pageNumber}`}
                          className="w-full h-full object-contain rounded"
                        />
                      ) : (
                        <FileText className="w-6 h-6 text-slate-400" />
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-700">
                        {pageNumber}
                      </span>
                      {selectedPages.includes(pageNumber) ? (
                        <CheckSquare className="w-3 h-3 text-red-500" />
                      ) : (
                        <Square className="w-3 h-3 text-slate-400" />
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
        <div className="flex items-center space-x-2 text-amber-800">
          <Zap className="w-4 h-4" />
          <span className="text-sm font-medium">
            Las páginas seleccionadas serán eliminadas permanentemente del PDF resultante
          </span>
        </div>
      </div>
    </motion.div>
  );
};

// Componente para extraer páginas de PDF
const PdfExtractPagesInterface = ({ file, onExtractPages, onCancel }) => {
  const [selectedPages, setSelectedPages] = useState([]);
  const [pageThumbnails, setPageThumbnails] = useState([]);
  const [isLoadingThumbnails, setIsLoadingThumbnails] = useState(true);

  // Cargar miniaturas del PDF
  useEffect(() => {
    const loadThumbnails = async () => {
      if (!file || !file.url) return;

      try {
        setIsLoadingThumbnails(true);

        if (!window.pdfjsLib) {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
          script.async = true;
          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        }

        const loadingTask = window.pdfjsLib.getDocument(file.url);
        const pdf = await loadingTask.promise;
        const totalPages = pdf.numPages;

        // Crear miniaturas para cada página
        const thumbnails = [];
        for (let i = 1; i <= Math.min(totalPages, 50); i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 0.2 });

          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({
            canvasContext: context,
            viewport: viewport
          }).promise;

          thumbnails.push({
            pageNumber: i,
            dataUrl: canvas.toDataURL(),
            width: viewport.width,
            height: viewport.height
          });
        }

        setPageThumbnails(thumbnails);

      } catch (error) {
        console.error('Error cargando miniaturas:', error);
      } finally {
        setIsLoadingThumbnails(false);
      }
    };

    loadThumbnails();
  }, [file]);

  const selectPage = (pageNumber) => {
    setSelectedPages(prev => {
      const isSelected = prev.includes(pageNumber);
      if (isSelected) {
        return prev.filter(p => p !== pageNumber);
      } else {
        return [...prev, pageNumber].sort((a, b) => a - b);
      }
    });
  };

  const selectAllPages = () => {
    const allPages = Array.from({ length: file.pages }, (_, i) => i + 1);
    setSelectedPages(allPages);
  };

  const clearSelection = () => {
    setSelectedPages([]);
  };

  const selectOddPages = () => {
    const oddPages = Array.from({ length: file.pages }, (_, i) => i + 1).filter(page => page % 2 === 1);
    setSelectedPages(oddPages);
  };

  const selectEvenPages = () => {
    const evenPages = Array.from({ length: file.pages }, (_, i) => i + 1).filter(page => page % 2 === 0);
    setSelectedPages(evenPages);
  };

  const selectFirstPage = () => {
    setSelectedPages([1]);
  };

  const selectLastPage = () => {
    setSelectedPages([file.pages]);
  };

  const handleExtractPages = () => {
    if (selectedPages.length === 0) {
      alert('Por favor, selecciona al menos una página para extraer');
      return;
    }

    onExtractPages(selectedPages);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl p-6 shadow-2xl border border-slate-200"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-bold text-slate-800 text-xl mb-2">
            Extraer Páginas: {file.name}
          </h3>
          <p className="text-slate-600">
            Selecciona las páginas que deseas extraer en un nuevo PDF
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onCancel}
            className="px-4 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-xl font-semibold transition-colors"
          >
            Cancelar
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleExtractPages}
            disabled={selectedPages.length === 0}
            className={`px-4 py-2 rounded-xl font-semibold transition-colors ${selectedPages.length === 0
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-amber-500 hover:bg-amber-600 text-white'
              }`}
          >
            Extraer ({selectedPages.length}) Páginas
          </motion.button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Panel izquierdo - Controles de selección */}
        <div className="space-y-4">
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <h4 className="font-semibold text-amber-800 mb-3">Selección Rápida</h4>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={selectAllPages}
                className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium"
              >
                Seleccionar Todas
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={clearSelection}
                className="px-3 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-lg text-sm font-medium"
              >
                Limpiar Selección
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={selectOddPages}
                className="px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium"
              >
                Páginas Impares
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={selectEvenPages}
                className="px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium"
              >
                Páginas Pares
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={selectFirstPage}
                className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium"
              >
                Primera Página
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={selectLastPage}
                className="px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium"
              >
                Última Página
              </motion.button>
            </div>
            {selectedPages.length > 0 && (
              <div className="mt-3">
                <p className="text-sm text-amber-700 font-medium mb-2">
                  {selectedPages.length} página(s) seleccionada(s) para extraer:
                </p>
                <div className="max-h-24 overflow-y-auto">
                  <div className="flex flex-wrap gap-1">
                    {selectedPages.map(page => (
                      <span
                        key={page}
                        className="bg-amber-100 text-amber-700 px-2 py-1 rounded text-xs font-medium"
                      >
                        {page}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-slate-50 rounded-xl p-4">
            <h4 className="font-semibold text-slate-800 mb-3">Información</h4>
            <div className="space-y-2 text-sm text-slate-600">
              <p>• Total de páginas: <strong>{file.pages}</strong></p>
              <p>• Páginas a extraer: <strong className="text-amber-600">{selectedPages.length}</strong></p>
              <p>• Nuevo PDF tendrá: <strong className="text-green-600">{selectedPages.length}</strong> página(s)</p>
            </div>
          </div>
        </div>

        {/* Panel derecho - Miniaturas de páginas */}
        <div className="bg-slate-50 rounded-xl p-4">
          <h4 className="font-semibold text-slate-800 mb-3">
            Selecciona Páginas a Extraer ({file.pages} páginas)
          </h4>

          {isLoadingThumbnails ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-8 h-8 text-amber-500 animate-spin mr-2" />
              <span className="text-slate-600">Cargando miniaturas...</span>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2 max-h-96 overflow-y-auto">
              {Array.from({ length: file.pages }, (_, i) => i + 1).map(pageNumber => (
                <motion.div
                  key={pageNumber}
                  whileHover={{ scale: 1.05 }}
                  className={`relative cursor-pointer rounded-lg border-2 transition-all ${selectedPages.includes(pageNumber)
                      ? 'border-amber-500 bg-amber-50 shadow-md'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  onClick={() => selectPage(pageNumber)}
                >
                  <div className="p-2">
                    <div className="bg-slate-100 rounded aspect-[3/4] flex items-center justify-center mb-1">
                      {pageThumbnails.find(t => t.pageNumber === pageNumber) ? (
                        <img
                          src={pageThumbnails.find(t => t.pageNumber === pageNumber).dataUrl}
                          alt={`Página ${pageNumber}`}
                          className="w-full h-full object-contain rounded"
                        />
                      ) : (
                        <FileText className="w-6 h-6 text-slate-400" />
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-700">
                        {pageNumber}
                      </span>
                      {selectedPages.includes(pageNumber) ? (
                        <CheckSquare className="w-3 h-3 text-amber-500" />
                      ) : (
                        <Square className="w-3 h-3 text-slate-400" />
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
        <div className="flex items-center space-x-2 text-amber-800">
          <Zap className="w-4 h-4" />
          <span className="text-sm font-medium">
            Las páginas seleccionadas serán extraídas en un nuevo PDF separado
          </span>
        </div>
      </div>
    </motion.div>
  );
};

// Componente para reordenar páginas de un PDF
const PdfReorderPagesInterface = ({ file, onReorderPages, onCancel }) => {
  const [pageOrder, setPageOrder] = useState([]);
  const [pageThumbnails, setPageThumbnails] = useState([]);
  const [isLoadingThumbnails, setIsLoadingThumbnails] = useState(true);

  // Inicializar el orden de páginas y cargar miniaturas
  useEffect(() => {
    const initializePageOrder = async () => {
      if (!file || !file.url) return;

      try {
        setIsLoadingThumbnails(true);

        if (!window.pdfjsLib) {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
          script.async = true;
          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        }

        const loadingTask = window.pdfjsLib.getDocument(file.url);
        const pdf = await loadingTask.promise;
        const totalPages = pdf.numPages;

        // Inicializar orden de páginas (1, 2, 3, ...)
        const initialOrder = Array.from({ length: totalPages }, (_, i) => i + 1);
        setPageOrder(initialOrder);

        // Crear miniaturas para cada página
        const thumbnails = [];
        for (let i = 1; i <= Math.min(totalPages, 50); i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 0.15 });

          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({
            canvasContext: context,
            viewport: viewport
          }).promise;

          thumbnails.push({
            pageNumber: i,
            dataUrl: canvas.toDataURL(),
            width: viewport.width,
            height: viewport.height
          });
        }

        setPageThumbnails(thumbnails);

      } catch (error) {
        console.error('Error cargando miniaturas:', error);
      } finally {
        setIsLoadingThumbnails(false);
      }
    };

    initializePageOrder();
  }, [file]);

  const movePageUp = (index) => {
    if (index === 0) return;
    const newOrder = [...pageOrder];
    [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
    setPageOrder(newOrder);
  };

  const movePageDown = (index) => {
    if (index === pageOrder.length - 1) return;
    const newOrder = [...pageOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setPageOrder(newOrder);
  };

  const shufflePages = () => {
    const shuffled = [...pageOrder];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setPageOrder(shuffled);
  };

  const reverseOrder = () => {
    setPageOrder([...pageOrder].reverse());
  };

  const resetOrder = () => {
    const originalOrder = Array.from({ length: file.pages }, (_, i) => i + 1);
    setPageOrder(originalOrder);
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));

    if (draggedIndex === targetIndex) return;

    const newOrder = [...pageOrder];
    const [movedItem] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, movedItem);
    setPageOrder(newOrder);
  };

  const handleReorder = () => {
    onReorderPages(pageOrder);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl p-6 shadow-2xl border border-slate-200"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-bold text-slate-800 text-xl mb-2">
            Reordenar Páginas: {file.name}
          </h3>
          <p className="text-slate-600">
            Arrastra las páginas o usa los botones para cambiar el orden
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onCancel}
            className="px-4 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-xl font-semibold transition-colors"
          >
            Cancelar
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleReorder}
            className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-semibold transition-colors"
          >
            Aplicar Orden
          </motion.button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Panel izquierdo - Controles de orden */}
        <div className="space-y-4">
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
            <h4 className="font-semibold text-purple-800 mb-3">Acciones Rápidas</h4>
            <div className="grid grid-cols-2 gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={shufflePages}
                className="px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium flex items-center justify-center space-x-2"
              >
                <Shuffle className="w-4 h-4" />
                <span>Mezclar Aleatorio</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={reverseOrder}
                className="px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium"
              >
                Invertir Orden
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={resetOrder}
                className="px-3 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-lg text-sm font-medium col-span-2"
              >
                Restaurar Orden Original
              </motion.button>
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl p-4">
            <h4 className="font-semibold text-slate-800 mb-3">Información del Orden</h4>
            <div className="space-y-2 text-sm text-slate-600">
              <p>• Total de páginas: <strong>{file.pages}</strong></p>
              <p>• Orden actual: <strong className="text-purple-600">{pageOrder.join(' → ')}</strong></p>
              <p>• Cambios: <strong className="text-green-600">
                {pageOrder.some((page, index) => page !== index + 1) ? 'Sí' : 'No'}
              </strong></p>
            </div>
          </div>

          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-3">Instrucciones</h4>
            <div className="space-y-2 text-sm text-blue-700">
              <div className="flex items-center space-x-2">
                <GripVertical className="w-4 h-4" />
                <span>Arrastra las páginas para cambiar el orden</span>
              </div>
              <div className="flex items-center space-x-2">
                <ArrowUp className="w-4 h-4" />
                <span>Usa las flechas para mover páginas individualmente</span>
              </div>
              <div className="flex items-center space-x-2">
                <Shuffle className="w-4 h-4" />
                <span>Usa las acciones rápidas para reordenamientos comunes</span>
              </div>
            </div>
          </div>
        </div>

        {/* Panel derecho - Lista de páginas ordenables */}
        <div className="bg-slate-50 rounded-xl p-4">
          <h4 className="font-semibold text-slate-800 mb-3">
            Orden de Páginas ({file.pages} páginas)
          </h4>

          {isLoadingThumbnails ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-8 h-8 text-purple-500 animate-spin mr-2" />
              <span className="text-slate-600">Cargando páginas...</span>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {pageOrder.map((pageNumber, index) => (
                <motion.div
                  key={`${pageNumber}-${index}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex items-center gap-3 p-3 bg-white rounded-xl border-2 transition-all ${pageNumber !== index + 1
                      ? 'border-purple-500 bg-purple-50 shadow-md'
                      : 'border-slate-200 hover:border-slate-300'
                    }`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  {/* Handle de arrastre y número de posición */}
                  <div className="flex flex-col items-center gap-1">
                    <GripVertical className="w-4 h-4 text-slate-400 cursor-grab" />
                    <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-2 py-1 rounded-full">
                      {index + 1}
                    </span>
                  </div>

                  {/* Miniatura de la página */}
                  <div className="w-16 h-20 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {pageThumbnails.find(t => t.pageNumber === pageNumber) ? (
                      <img
                        src={pageThumbnails.find(t => t.pageNumber === pageNumber).dataUrl}
                        alt={`Página ${pageNumber}`}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <FileText className="w-6 h-6 text-slate-400" />
                    )}
                  </div>

                  {/* Información de la página */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-semibold text-slate-800 text-sm">
                        Página {pageNumber}
                      </h4>
                      {pageNumber !== index + 1 && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                          Movida
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-slate-600 mt-1">
                      <span>Posición original: {pageNumber}</span>
                      <span>•</span>
                      <span>Nueva posición: {index + 1}</span>
                    </div>
                  </div>

                  {/* Controles de movimiento */}
                  <div className="flex items-center gap-1">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => movePageUp(index)}
                      disabled={index === 0}
                      className={`p-1 rounded-lg transition-colors ${index === 0
                          ? 'text-slate-300 cursor-not-allowed'
                          : 'text-slate-600 hover:text-purple-600 hover:bg-purple-50'
                        }`}
                      title="Mover arriba"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => movePageDown(index)}
                      disabled={index === pageOrder.length - 1}
                      className={`p-1 rounded-lg transition-colors ${index === pageOrder.length - 1
                          ? 'text-slate-300 cursor-not-allowed'
                          : 'text-slate-600 hover:text-purple-600 hover:bg-purple-50'
                        }`}
                      title="Mover abajo"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
        <div className="flex items-center space-x-2 text-amber-800">
          <Zap className="w-4 h-4" />
          <span className="text-sm font-medium">
            El nuevo orden se aplicará al PDF resultante. Puedes previsualizar el orden actual en la lista.
          </span>
        </div>
      </div>
    </motion.div>
  );
};

// Componente principal
const Documents = () => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [viewingPdf, setViewingPdf] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processResult, setProcessResult] = useState(null);
  const [showReorderInterface, setShowReorderInterface] = useState(false);
  const [showSplitInterface, setShowSplitInterface] = useState(false);
  const [showDeleteInterface, setShowDeleteInterface] = useState(false);
  const [showExtractInterface, setShowExtractInterface] = useState(false);
  const [showReorderPagesInterface, setShowReorderPagesInterface] = useState(false);
  const [fileToProcess, setFileToProcess] = useState(null);
  const fileInputRef = useRef(null);

  const API_BASE_URL = 'http://localhost:3000';

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragging(true);
    } else if (e.type === "dragleave") {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files) => {
    const pdfFiles = Array.from(files).filter(file => file.type === 'application/pdf');

    if (pdfFiles.length === 0) {
      alert('Por favor, selecciona archivos PDF');
      return;
    }

    setIsUploading(true);
    setProcessResult(null);

    pdfFiles.forEach((file, index) => {
      setTimeout(() => {
        const newFile = {
          id: Date.now() + index,
          name: file.name,
          size: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
          type: file.type,
          pages: Math.floor(Math.random() * 20) + 1,
          uploadDate: new Date().toLocaleTimeString(),
          file: file,
          url: URL.createObjectURL(file)
        };

        setUploadedFiles(prev => [...prev, newFile]);

        if (index === pdfFiles.length - 1) {
          setIsUploading(false);
        }
      }, 800 * (index + 1));
    });
  };

  const removeFile = (fileId) => {
    const fileToRemove = uploadedFiles.find(file => file.id === fileId);
    if (fileToRemove && fileToRemove.url) {
      URL.revokeObjectURL(fileToRemove.url);
    }
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
    if (viewingPdf?.id === fileId) {
      setViewingPdf(null);
    }
  };

  const removeAllFiles = () => {
    uploadedFiles.forEach(file => {
      if (file.url) {
        URL.revokeObjectURL(file.url);
      }
    });
    setUploadedFiles([]);
    setSelectedAction(null);
    setViewingPdf(null);
    setProcessResult(null);
    setShowReorderInterface(false);
    setShowSplitInterface(false);
    setShowDeleteInterface(false);
    setShowExtractInterface(false);
    setShowReorderPagesInterface(false);
  };

  const reorderFiles = (newFilesOrder) => {
    setUploadedFiles(newFilesOrder);
  };

  // Función para unir PDFs usando tu API
  const mergePdfs = async () => {
    if (uploadedFiles.length < 2) {
      alert('Se necesitan al menos 2 PDFs para unir');
      return;
    }

    setIsProcessing(true);
    setSelectedAction('combine');

    try {
      const formData = new FormData();

      // Agregar todos los archivos PDF al FormData en el orden actual
      uploadedFiles.forEach((file) => {
        formData.append('pdfs', file.file);
      });

      const response = await fetch(`${API_BASE_URL}/api/pdf/merge`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
      }

      // Descargar el PDF resultante
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pdf-unido-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setProcessResult({
        type: 'success',
        message: 'PDFs unidos exitosamente'
      });

    } catch (error) {
      console.error('Error al unir PDFs:', error);
      setProcessResult({
        type: 'error',
        message: 'Error al unir los PDFs: ' + error.message
      });
    } finally {
      setIsProcessing(false);
      setTimeout(() => setProcessResult(null), 5000);
    }
  };

  // Función para dividir PDFs
  const splitPdf = async (ranges) => {
    if (!fileToProcess) return;

    setIsProcessing(true);
    setSelectedAction('split');

    try {
      const formData = new FormData();
      formData.append('pdf', fileToProcess.file);
      formData.append('ranges', ranges);

      const response = await fetch(`${API_BASE_URL}/api/pdf/split`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Error desconocido al dividir el PDF');
      }

      // Descargar todos los archivos resultantes usando las URLs proporcionadas
      result.files.forEach((file, index) => {
        setTimeout(() => {
          const link = document.createElement('a');
          link.href = file.url;
          link.download = file.fileName;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }, index * 1000);
      });

      setProcessResult({
        type: 'success',
        message: `PDF dividido en ${result.files.length} archivos. Las descargas comenzarán automáticamente.`
      });

    } catch (error) {
      console.error('Error al dividir PDF:', error);
      setProcessResult({
        type: 'error',
        message: 'Error al dividir el PDF: ' + error.message
      });
    } finally {
      setIsProcessing(false);
      setShowSplitInterface(false);
      setFileToProcess(null);
      setTimeout(() => setProcessResult(null), 8000);
    }
  };

  // Función para eliminar páginas de PDF
  const deletePdfPages = async (pagesToRemove) => {
    if (!fileToProcess) return;

    setIsProcessing(true);
    setSelectedAction('delete-pages');

    try {
      const formData = new FormData();
      formData.append('pdf', fileToProcess.file);
      formData.append('pagesToRemove', JSON.stringify(pagesToRemove));

      const response = await fetch(`${API_BASE_URL}/api/pdf/remove-pages`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
      }

      // Descargar el PDF resultante
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pdf-sin-paginas-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setProcessResult({
        type: 'success',
        message: `Se eliminaron ${pagesToRemove.length} páginas exitosamente`
      });

    } catch (error) {
      console.error('Error al eliminar páginas:', error);
      setProcessResult({
        type: 'error',
        message: 'Error al eliminar las páginas: ' + error.message
      });
    } finally {
      setIsProcessing(false);
      setShowDeleteInterface(false);
      setFileToProcess(null);
      setTimeout(() => setProcessResult(null), 5000);
    }
  };

  // Función para extraer páginas de PDF
  const extractPdfPages = async (pagesToExtract) => {
    if (!fileToProcess) return;

    setIsProcessing(true);
    setSelectedAction('extract-pages');

    try {
      const formData = new FormData();
      formData.append('pdf', fileToProcess.file);
      formData.append('pagesToExtract', JSON.stringify(pagesToExtract));

      const response = await fetch(`${API_BASE_URL}/api/pdf/extract-pages`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
      }

      // Descargar el PDF resultante
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `paginas-extraidas-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setProcessResult({
        type: 'success',
        message: `Se extrajeron ${pagesToExtract.length} páginas exitosamente`
      });

    } catch (error) {
      console.error('Error al extraer páginas:', error);
      setProcessResult({
        type: 'error',
        message: 'Error al extraer las páginas: ' + error.message
      });
    } finally {
      setIsProcessing(false);
      setShowExtractInterface(false);
      setFileToProcess(null);
      setTimeout(() => setProcessResult(null), 5000);
    }
  };

  // Función para reordenar páginas de PDF
  const reorderPdfPages = async (pageOrder) => {
    if (!fileToProcess) return;

    setIsProcessing(true);
    setSelectedAction('reorder');

    try {
      const formData = new FormData();
      formData.append('pdf', fileToProcess.file);
      formData.append('pages', JSON.stringify(pageOrder));

      const response = await fetch(`${API_BASE_URL}/api/pdf/reorder`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
      }

      // Descargar el PDF resultante
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pdf-reordenado-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setProcessResult({
        type: 'success',
        message: `PDF reordenado exitosamente con ${pageOrder.length} páginas`
      });

    } catch (error) {
      console.error('Error al reordenar páginas:', error);
      setProcessResult({
        type: 'error',
        message: 'Error al reordenar las páginas: ' + error.message
      });
    } finally {
      setIsProcessing(false);
      setShowReorderPagesInterface(false);
      setFileToProcess(null);
      setTimeout(() => setProcessResult(null), 5000);
    }
  };

  const handleAction = async (action) => {
    if (uploadedFiles.length === 0) return;

    setProcessResult(null);

    switch (action) {
      case 'combine':
        if (uploadedFiles.length >= 2) {
          setShowReorderInterface(true);
        } else {
          alert('Se necesitan al menos 2 PDFs para unir');
        }
        break;

      case 'split':
        if (uploadedFiles.length === 1) {
          setFileToProcess(uploadedFiles[0]);
          setShowSplitInterface(true);
        } else {
          alert('Selecciona exactamente un PDF para dividir');
        }
        break;

      case 'delete-pages':
        if (uploadedFiles.length === 1) {
          setFileToProcess(uploadedFiles[0]);
          setShowDeleteInterface(true);
        } else {
          alert('Selecciona exactamente un PDF para eliminar páginas');
        }
        break;

      case 'extract-pages':
        if (uploadedFiles.length === 1) {
          setFileToProcess(uploadedFiles[0]);
          setShowExtractInterface(true);
        } else {
          alert('Selecciona exactamente un PDF para extraer páginas');
        }
        break;

      case 'reorder':
        if (uploadedFiles.length === 1) {
          setFileToProcess(uploadedFiles[0]);
          setShowReorderPagesInterface(true);
        } else {
          alert('Selecciona exactamente un PDF para reordenar páginas');
        }
        break;

      case 'scan':
        /// console.log('Escanear a PDF');
        break;

      default:
      /// console.log(`Acción no implementada: ${action}`);
    }
  };

  const confirmMerge = () => {
    setShowReorderInterface(false);
    mergePdfs();
  };

  const cancelMerge = () => {
    setShowReorderInterface(false);
  };

  const cancelSplit = () => {
    setShowSplitInterface(false);
    setFileToProcess(null);
  };

  const cancelDelete = () => {
    setShowDeleteInterface(false);
    setFileToProcess(null);
  };

  const cancelExtract = () => {
    setShowExtractInterface(false);
    setFileToProcess(null);
  };

  const cancelReorderPages = () => {
    setShowReorderPagesInterface(false);
    setFileToProcess(null);
  };

  const viewPdf = (file) => {
    setViewingPdf(file);
    setCurrentPage(1);
    setIsPlaying(false);
    setZoom(100);
  };

  const closePdfViewer = () => {
    setViewingPdf(null);
    setCurrentPage(1);
    setIsPlaying(false);
    setZoom(100);
  };

  const nextPage = () => {
    if (viewingPdf && currentPage < viewingPdf.pages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const zoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 200));
  };

  const zoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50));
  };

  const resetZoom = () => {
    setZoom(100);
  };

  const downloadPdf = (file) => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.name;
    link.click();
  };

  // Simular reproducción automática
  React.useEffect(() => {
    let interval;
    if (isPlaying && viewingPdf) {
      interval = setInterval(() => {
        setCurrentPage(prev => {
          if (prev >= viewingPdf.pages) {
            setIsPlaying(false);
            return 1;
          }
          return prev + 1;
        });
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, viewingPdf]);

  const tools = [
    {
      id: 'combine',
      name: 'Unir PDF',
      description: 'Combina múltiples PDF en uno solo',
      icon: Combine,
      color: 'from-blue-500 to-cyan-500',
      minFiles: 2
    },
    {
      id: 'split',
      name: 'Dividir PDF',
      description: 'Divide tu PDF en documentos separados',
      icon: Split,
      color: 'from-emerald-500 to-green-500',
      minFiles: 1,
      maxFiles: 1
    },
    {
      id: 'delete-pages',
      name: 'Eliminar páginas',
      description: 'Remueve páginas específicas del PDF',
      icon: Trash2,
      color: 'from-red-500 to-rose-500',
      minFiles: 1,
      maxFiles: 1
    },
    {
      id: 'extract-pages',
      name: 'Extraer páginas',
      description: 'Extrae páginas específicas en nuevo PDF',
      icon: Scissors,
      color: 'from-amber-500 to-orange-500',
      minFiles: 1,
      maxFiles: 1
    },
    {
      id: 'reorder',
      name: 'Ordenar PDF',
      description: 'Reorganiza el orden de las páginas',
      icon: Move,
      color: 'from-purple-500 to-pink-500',
      minFiles: 1,
      maxFiles: 1
    },
    {
      id: 'scan',
      name: 'Escanea a PDF',
      description: 'Convierte imágenes escaneadas a PDF',
      icon: Scan,
      color: 'from-indigo-500 to-blue-500',
      minFiles: 1
    }
  ];

  const isToolDisabled = (tool) => {
    if (tool.maxFiles && uploadedFiles.length > tool.maxFiles) return true;
    if (tool.minFiles && uploadedFiles.length < tool.minFiles) return true;
    return false;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-4">
            Editor de PDF
          </h1>
          <p className="text-slate-600 text-lg">
            Sube uno o múltiples PDFs y elige la acción que deseas realizar
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Panel izquierdo - Carga y gestión de PDFs */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            {/* Área de carga */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className={`relative border-3 border-dashed rounded-3xl p-8 text-center transition-all duration-300 ${isDragging
                ? 'border-red-400 bg-red-50/50 scale-105'
                : 'border-slate-300 bg-white hover:border-red-300 hover:bg-red-50/30'
                } shadow-2xl`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                multiple
                onChange={handleFileSelect}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />

              <div className="space-y-6">
                {/* Icono principal */}
                <motion.div
                  animate={isUploading ? { rotate: 360 } : { rotate: 0 }}
                  transition={{ duration: 2, repeat: isUploading ? Infinity : 0 }}
                  className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-500 to-rose-500 rounded-3xl shadow-2xl"
                >
                  {isUploading ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-8 h-8 border-4 border-white border-t-transparent rounded-full"
                    />
                  ) : (
                    <Upload className="w-8 h-8 text-white" />
                  )}
                </motion.div>

                {/* Texto principal */}
                <div className="space-y-3">
                  <motion.h3
                    className="text-2xl font-bold text-slate-800"
                    animate={isDragging ? { scale: 1.05 } : { scale: 1 }}
                  >
                    {isUploading ? 'Subiendo PDFs...' : 'Selecciona tus PDFs'}
                  </motion.h3>
                  <p className="text-slate-600">
                    {isUploading
                      ? 'Procesando tus documentos...'
                      : 'Arrastra y suelta tus archivos aquí o haz clic para seleccionar'
                    }
                  </p>
                  <p className="text-sm text-slate-500">
                    Puedes seleccionar múltiples archivos PDF
                  </p>
                </div>

                {/* Contador de archivos */}
                {uploadedFiles.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center justify-center space-x-4"
                  >
                    <div className="bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {uploadedFiles.length} PDF{uploadedFiles.length !== 1 ? 's' : ''} subido{uploadedFiles.length !== 1 ? 's' : ''}
                    </div>
                    <motion.button
                      onClick={removeAllFiles}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex items-center space-x-1 px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded-full text-sm font-semibold transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                      <span>Eliminar todos</span>
                    </motion.button>
                  </motion.div>
                )}
              </div>
            </motion.div>

            {/* Lista de PDFs subidos */}
            <AnimatePresence>
              {uploadedFiles.length > 0 && !showReorderInterface && !showSplitInterface && !showDeleteInterface && !showExtractInterface && !showReorderPagesInterface && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-white rounded-3xl p-6 shadow-2xl border border-slate-200"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-800 text-lg">
                      Archivos Subidos
                    </h3>
                    <span className="text-sm text-slate-500">
                      {uploadedFiles.length} archivo{uploadedFiles.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {uploadedFiles.map((file, index) => (
                      <motion.div
                        key={file.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-200 group hover:bg-white hover:shadow-md transition-all"
                      >
                        <div className="flex items-center space-x-4 flex-1 min-w-0">
                          <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-rose-500 rounded-xl flex items-center justify-center flex-shrink-0">
                            <FileText className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-slate-800 truncate text-sm">
                              {file.name}
                            </h4>
                            <div className="flex items-center space-x-3 text-xs text-slate-600 mt-1">
                              <span>{file.size}</span>
                              <span>•</span>
                              <span>{file.pages} páginas</span>
                              <span>•</span>
                              <span>{file.uploadDate}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => viewPdf(file)}
                            className="p-2 text-blue-500 hover:text-blue-600 transition-colors"
                            title="Ver PDF"
                          >
                            <Eye className="w-4 h-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => downloadPdf(file)}
                            className="p-2 text-green-500 hover:text-green-600 transition-colors"
                            title="Descargar PDF"
                          >
                            <Download className="w-4 h-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => removeFile(file.id)}
                            className="p-2 text-red-400 hover:text-red-600 transition-colors"
                            title="Eliminar archivo"
                          >
                            <X className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Interfaz de reordenamiento para unir PDFs */}
            <AnimatePresence>
              {showReorderInterface && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="bg-white rounded-3xl p-6 shadow-2xl border border-slate-200"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="font-bold text-slate-800 text-xl mb-2">
                        Ordenar PDFs para unir
                      </h3>
                      <p className="text-slate-600">
                        Arrastra los PDFs o usa los botones para definir el orden de unión
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={cancelMerge}
                        className="px-4 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-xl font-semibold transition-colors"
                      >
                        Cancelar
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={confirmMerge}
                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold transition-colors"
                      >
                        Unir PDFs
                      </motion.button>
                    </div>
                  </div>

                  <div className="max-h-96 overflow-y-auto">
                    <PdfReorderList
                      files={uploadedFiles}
                      onReorder={reorderFiles}
                      onRemove={removeFile}
                    />
                  </div>

                  <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <div className="flex items-center space-x-2 text-blue-800">
                      <GripVertical className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        Consejo: Arrastra los PDFs o usa las flechas para cambiar el orden
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Interfaz de división de PDF */}
            <AnimatePresence>
              {showSplitInterface && fileToProcess && (
                <PdfSplitInterface
                  file={fileToProcess}
                  onSplit={splitPdf}
                  onCancel={cancelSplit}
                />
              )}
            </AnimatePresence>

            {/* Interfaz de eliminación de páginas */}
            <AnimatePresence>
              {showDeleteInterface && fileToProcess && (
                <PdfDeletePagesInterface
                  file={fileToProcess}
                  onDeletePages={deletePdfPages}
                  onCancel={cancelDelete}
                />
              )}
            </AnimatePresence>

            {/* Interfaz de extracción de páginas */}
            <AnimatePresence>
              {showExtractInterface && fileToProcess && (
                <PdfExtractPagesInterface
                  file={fileToProcess}
                  onExtractPages={extractPdfPages}
                  onCancel={cancelExtract}
                />
              )}
            </AnimatePresence>

            {/* Interfaz de reordenamiento de páginas */}
            <AnimatePresence>
              {showReorderPagesInterface && fileToProcess && (
                <PdfReorderPagesInterface
                  file={fileToProcess}
                  onReorderPages={reorderPdfPages}
                  onCancel={cancelReorderPages}
                />
              )}
            </AnimatePresence>

            {/* Información adicional */}
            {!showReorderInterface && !showSplitInterface && !showDeleteInterface && !showExtractInterface && !showReorderPagesInterface && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <Zap className="w-5 h-5 text-amber-500" />
                  <h3 className="font-semibold text-slate-800">Características</h3>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center space-x-2 text-slate-600">
                    <Shield className="w-4 h-4 text-emerald-500" />
                    <span>Procesamiento seguro</span>
                  </div>
                  <div className="flex items-center space-x-2 text-slate-600">
                    <Download className="w-4 h-4 text-blue-500" />
                    <span>Descarga inmediata</span>
                  </div>
                  <div className="flex items-center space-x-2 text-slate-600">
                    <Plus className="w-4 h-4 text-red-500" />
                    <span>Múltiples PDFs</span>
                  </div>
                  <div className="flex items-center space-x-2 text-slate-600">
                    <FileText className="w-4 h-4 text-purple-500" />
                    <span>Visualización completa</span>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Panel derecho - Herramientas */}
          {!showReorderInterface && !showSplitInterface && !showDeleteInterface && !showExtractInterface && !showReorderPagesInterface && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-6"
            >
              <div className="bg-white rounded-3xl p-8 shadow-2xl border border-slate-200">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-slate-800 mb-2">
                    Herramientas PDF
                  </h2>
                  <p className="text-slate-600">
                    Selecciona la acción que deseas realizar con tus documentos
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {tools.map((tool, index) => {
                    const Icon = tool.icon;
                    const disabled = isToolDisabled(tool);

                    return (
                      <motion.button
                        key={tool.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        whileHover={{
                          scale: !disabled ? 1.05 : 1,
                          y: !disabled ? -2 : 0
                        }}
                        whileTap={{ scale: !disabled ? 0.95 : 1 }}
                        onClick={() => !disabled && handleAction(tool.id)}
                        disabled={disabled}
                        className={`p-4 rounded-2xl text-left transition-all duration-300 ${!disabled
                          ? 'bg-slate-50 hover:bg-white hover:shadow-lg border-2 border-transparent hover:border-slate-200 cursor-pointer'
                          : 'bg-slate-100 opacity-50 cursor-not-allowed'
                          } group relative overflow-hidden`}
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${tool.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />

                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 bg-gradient-to-br ${tool.color} rounded-xl flex items-center justify-center shadow-sm`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h3 className="font-semibold text-slate-800 text-sm">
                                {tool.name}
                              </h3>
                              {tool.minFiles > 1 && (
                                <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded-full">
                                  {tool.minFiles}+ PDFs
                                </span>
                              )}
                              {tool.maxFiles === 1 && (
                                <span className="text-xs bg-amber-200 text-amber-600 px-2 py-1 rounded-full">
                                  1 PDF
                                </span>
                              )}
                            </div>
                            <p className="text-slate-600 text-xs mt-1">
                              {tool.description}
                            </p>
                          </div>
                          <ArrowRight className={`w-4 h-4 transition-transform duration-300 ${!disabled ? 'text-slate-400 group-hover:translate-x-1' : 'text-slate-300'
                            }`} />
                        </div>
                      </motion.button>
                    );
                  })}
                </div>

                <AnimatePresence>
                  {uploadedFiles.length === 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="text-center mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200"
                    >
                      <p className="text-amber-800 text-sm">
                        Sube al menos un PDF para habilitar las herramientas
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Estado del procesamiento */}
              <AnimatePresence>
                {isProcessing && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-3xl p-6 text-white shadow-2xl"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-lg">
                          {selectedAction === 'combine' ? 'Uniendo PDFs...' :
                            selectedAction === 'split' ? 'Dividiendo PDF...' :
                              selectedAction === 'delete-pages' ? 'Eliminando páginas...' :
                                selectedAction === 'extract-pages' ? 'Extrayendo páginas...' :
                                  'Reordenando páginas...'}
                        </h3>
                        <p className="text-blue-100 text-sm">
                          Procesando {uploadedFiles.length} documento{uploadedFiles.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        className="w-8 h-8 border-2 border-white border-t-transparent rounded-full"
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Resultado del procesamiento */}
              <AnimatePresence>
                {processResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    className={`rounded-3xl p-6 shadow-2xl ${processResult.type === 'success'
                      ? 'bg-gradient-to-br from-emerald-500 to-green-500 text-white'
                      : 'bg-gradient-to-br from-red-500 to-rose-500 text-white'
                      }`}
                  >
                    <div className="flex items-center space-x-3">
                      {processResult.type === 'success' ? (
                        <CheckCircle className="w-6 h-6" />
                      ) : (
                        <X className="w-6 h-6" />
                      )}
                      <div>
                        <h3 className="font-bold text-lg">
                          {processResult.type === 'success' ? '¡Éxito!' : 'Error'}
                        </h3>
                        <p className="text-sm opacity-90">
                          {processResult.message}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </div>

      {/* Visor de PDF Modal */}
      <AnimatePresence>
        {viewingPdf && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className={`bg-white rounded-xl sm:rounded-3xl shadow-2xl flex flex-col ${isFullscreen ? 'w-full h-full' : 'w-full h-full sm:max-w-6xl sm:max-h-[90vh] max-h-[95vh]'
                }`}
            >
              {/* Header del visor */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-6 border-b border-slate-200 gap-3">
                <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                  <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-slate-800 text-sm sm:text-lg truncate">
                      {viewingPdf.name}
                    </h3>
                    <p className="text-slate-600 text-xs sm:text-sm">
                      Página {currentPage} de {viewingPdf.pages} • Zoom: {zoom}%
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2 flex-wrap gap-1">
                  {/* Controles de zoom */}
                  <div className="flex items-center space-x-1 bg-slate-100 rounded-lg p-1">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={zoomOut}
                      disabled={zoom <= 50}
                      className="p-1 rounded-md disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Zoom Out"
                    >
                      <ZoomOut className="w-3 h-3 sm:w-4 sm:h-4 text-slate-600" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={resetZoom}
                      className="p-1 rounded-md"
                      title="Reset Zoom"
                    >
                      <RotateCw className="w-3 h-3 sm:w-4 sm:h-4 text-slate-600" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={zoomIn}
                      disabled={zoom >= 200}
                      className="p-1 rounded-md disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Zoom In"
                    >
                      <ZoomIn className="w-3 h-3 sm:w-4 sm:h-4 text-slate-600" />
                    </motion.button>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={togglePlay}
                    className="p-1.5 sm:p-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                    title={isPlaying ? 'Pausar' : 'Reproducir automáticamente'}
                  >
                    {isPlaying ? (
                      <Pause className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
                    ) : (
                      <Play className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
                    )}
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => downloadPdf(viewingPdf)}
                    className="p-1.5 sm:p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                    title="Descargar PDF"
                  >
                    <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={toggleFullscreen}
                    className="p-1.5 sm:p-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors hidden sm:block"
                    title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
                  >
                    {isFullscreen ? (
                      <Minimize className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
                    ) : (
                      <Maximize className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
                    )}
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={closePdfViewer}
                    className="p-1.5 sm:p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4 sm:w-5 sm:h-5" />
                  </motion.button>
                </div>
              </div>

              {/* Área del PDF */}
              <div className="flex-1 bg-slate-900 overflow-auto">
                <div className="min-h-full flex items-start justify-center p-2 sm:p-4 md:p-8">
                  <div
                    className="transition-transform duration-300 origin-top w-full max-w-full"
                    style={{ transform: `scale(${zoom / 100})` }}
                  >
                    {/* Visor de PDF real */}
                    <SimplePdfViewer
                      file={viewingPdf}
                      currentPage={currentPage}
                      onPageChange={setCurrentPage}
                      zoom={zoom}
                    />
                  </div>
                </div>
              </div>

              {/* Controles de navegación */}
              <div className="flex items-center justify-between p-3 sm:p-6 border-t border-slate-200 bg-white gap-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-colors text-sm sm:text-base ${currentPage === 1
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-red-500 hover:bg-red-600 text-white'
                    }`}
                >
                  <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Anterior</span>
                  <span className="sm:hidden">Ant</span>
                </motion.button>

                <div className="flex items-center space-x-2 sm:space-x-4">
                  <span className="text-slate-700 font-medium text-xs sm:text-lg text-center">
                    Pág. {currentPage} / {viewingPdf.pages}
                  </span>
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={nextPage}
                  disabled={currentPage === viewingPdf.pages}
                  className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-colors text-sm sm:text-base ${currentPage === viewingPdf.pages
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-red-500 hover:bg-red-600 text-white'
                    }`}
                >
                  <span className="hidden sm:inline">Siguiente</span>
                  <span className="sm:hidden">Sig</span>
                  <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Documents;